import React, { useCallback, useEffect } from 'react';
import { useFocusEffect } from 'expo-router';

import { Bag } from '@order/features-ordering';
import { getStripeKeyPrefix, StripeElementsProvider } from '@order/integrations-stripe';
import { useFeatureFlag } from '@order/LaunchDarkly';

import { useIsLoggedIn } from '../../context/AuthMachine';
import { useCloseBag, useDeliveryOrderInFlight } from '../../hooks';
import { handleHardwareBackButtonPress } from '../../navigation';
import { BagAnimatedDialog, BagContent } from './components';
import {
  useBagGestures,
  useBagCartPollingControl,
  useOrderState,
  useWantedTimePollingControl,
} from './hooks';
import { getOrderPlaybackConfig } from './utils';

export const BagScreen = () => {
  const orderPlaybackConfigFlag = useFeatureFlag('CELS-4127-order-playback-config'); // prettier-ignore

  const { orderSubmissionDelay } = getOrderPlaybackConfig(orderPlaybackConfigFlag);

  const orderState = useOrderState({ orderSubmissionDelay });

  const {
    cart,
    isFetchingInitialData,
    isSubmittingOrder,
    isShowingModal,
    isShowingUpsellInterstitial,
    startWantedTimesPolling,
    stopWantedTimesPolling,
    startBagCartPolling,
    stopBagCartPolling,
    startOrdering,
    cancelRequest,
    purchaseAttempt,
    stripeSession,
    total,
  } = orderState;

  const isLoggedIn = useIsLoggedIn();
  const isBagReady = !isFetchingInitialData && cart?.orderId;
  const shouldRenderAnimatedDialog = Boolean(
    isBagReady && isLoggedIn && !isShowingUpsellInterstitial,
  );

  // ──────────────────────────────────────────────────────────────────────────

  const closeBag = useCloseBag();

  // Prevent closing bag while submitting an order.
  const handleCloseBag = useCallback(() => {
    if (isSubmittingOrder) return;

    closeBag();
  }, [isSubmittingOrder, closeBag]);

  // ─── Effects ───────────────────────────────────────────────────────────────

  // Updates the in-flight delivery order status.
  useDeliveryOrderInFlight();

  // Enable/disable gestures when modal is shown.
  useBagGestures(isShowingModal);

  // Fetch ordering data on bag focus.
  useFocusEffect(startOrdering);

  // Cancel bag actions if the customer presses the hardware back button.
  const beforeScreenRemoval = useCallback(() => {
    if (isShowingModal) {
      cancelRequest();

      return true;
    }

    return false;
  }, [isShowingModal, cancelRequest]);

  useEffect(() => {
    return handleHardwareBackButtonPress(beforeScreenRemoval);
  }, [beforeScreenRemoval]);

  // Manage wanted times polling.
  useWantedTimePollingControl({
    startWantedTimesPolling,
    stopWantedTimesPolling,
  });

  // Manage cart polling.
  useBagCartPollingControl({
    startBagCartPolling,
    stopBagCartPolling,
  });

  // ──────────────────────────────────────────────────────────────────────────

  return (
    <Bag.DynamicModal onClose={handleCloseBag}>
      <BagContent orderState={orderState} />

      {shouldRenderAnimatedDialog ? (
        <StripeElementsProvider amount={total} sessionId={stripeSession}>
          <BagAnimatedDialog key={getStripeKeyPrefix(purchaseAttempt)} orderState={orderState} />
        </StripeElementsProvider>
      ) : null}
    </Bag.DynamicModal>
  );
};
